import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";

export default function Cheques() {
  const pinPrompt = usePinPrompt();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ direction: "in", party_name: "", cheque_number: "", bank_name: "", amount: 0, date: new Date().toISOString().slice(0, 10), status: "pending" });

  const load = async () => setRows((await api.get("/cheques")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    await pinPrompt();
    try { await api.post("/cheques", { ...f, date: new Date(f.date).toISOString() }); toast.success("Cheque recorded"); setOpen(false); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const changeStatus = async (c, status) => {
    await pinPrompt();
    try { await api.patch(`/cheques/${c._id_str}`, { ...c, status }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await pinPrompt();
    try { await api.delete(`/cheques/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <div className="overline">Cheques Register</div>
          <h1 className="text-3xl font-bold tracking-tight">Cheques In / Out</h1>
        </div>
        <Button className="btn-primary" onClick={() => setOpen(true)} data-testid="cheque-add"><Plus size={14} /> Add Cheque</Button>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="acc-table w-full">
          <thead><tr><th>Date</th><th>Dir</th><th>Cheque #</th><th>Bank</th><th>Party</th><th className="num">Amount</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c._id_str} data-testid={`cheque-row-${c._id_str}`}>
                <td>{new Date(c.date).toLocaleDateString()}</td>
                <td className="uppercase text-xs">{c.direction}</td>
                <td className="font-mono">{c.cheque_number}</td>
                <td>{c.bank_name}</td>
                <td>{c.party_name}</td>
                <td className="num">{fmtINR(c.amount)}</td>
                <td>
                  <span className={`chip ${c.status === "cleared" ? "chip-green" : c.status === "bounced" ? "chip-red" : "chip-amber"}`}>{c.status}</span>
                </td>
                <td>
                  <div className="flex gap-1">
                    {c.status !== "cleared" && <Button size="sm" variant="outline" className="h-7" onClick={() => changeStatus(c, "cleared")}>Clear</Button>}
                    {c.status !== "bounced" && <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => changeStatus(c, "bounced")}>Bounce</Button>}
                    <button className="p-1 text-red-600 hover:bg-red-50 rounded" onClick={() => del(c._id_str)}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">No cheques recorded.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Cheque</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Direction</Label>
              <select className="w-full border border-border rounded-md h-9 px-2 bg-white" value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })}>
                <option value="in">In (received)</option><option value="out">Out (issued)</option>
              </select>
            </div>
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
            <div><Label>Cheque Number*</Label><Input value={f.cheque_number} onChange={(e) => setF({ ...f, cheque_number: e.target.value })} /></div>
            <div><Label>Bank</Label><Input value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} /></div>
            <div><Label>Party</Label><Input value={f.party_name} onChange={(e) => setF({ ...f, party_name: e.target.value })} /></div>
            <div><Label>Amount</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: +e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="cheque-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
